import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ArrowLeft, Download, Printer, CheckCircle2, XCircle, 
  Lightbulb, FileText, Image as ImageIcon, Layout, AlertTriangle,
  ExternalLink, BookOpen
} from "lucide-react";

const HelpPage = () => {
  const navigate = useNavigate();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50" data-testid="help-page">
      {/* Header - Hidden in print */}
      <header className="print:hidden sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="rounded-full hover:bg-slate-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/faq`, '_blank')}
              className="rounded-full border-slate-200"
              data-testid="download-faq-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download FAQ
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              className="rounded-full border-slate-200"
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print / Save PDF
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-10 print:py-4">
        {/* Title Section */}
        <div className="text-center mb-12 print:mb-8">
          <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4 print:hidden">
            <BookOpen className="w-8 h-8 text-violet-600" />
          </div>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
            How to Create Email-Friendly Web Pages
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A simple guide to help you structure your web pages so our email generator 
            can create beautiful newsletters automatically.
          </p>
        </div>

        {/* Quick Summary Card */}
        <Card className="bg-gradient-to-br from-violet-50 to-indigo-50 border-violet-100 rounded-2xl mb-8 print:bg-violet-50 print:border print:border-violet-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
              <Lightbulb className="w-5 h-5 text-amber-500" />
              The Quick Version
            </h2>
            <p className="text-slate-700 mb-4">
              Our system works best when your web page has:
            </p>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>A clear headline</strong> at the top (this becomes your email subject)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Several paragraphs</strong> of text (these become your email sections)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Good quality images</strong> (we'll grab these for your email)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Simple, readable text</strong> (not too fancy or embedded in images)</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Ideal Page Structure */}
        <Card className="bg-white border-slate-100 rounded-2xl mb-8 print:border print:border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
              <Layout className="w-5 h-5 text-violet-600" />
              The Ideal Page Structure
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-slate-600">
              Think of your web page like a newspaper article. Here's the perfect structure:
            </p>

            {/* Visual Example */}
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 bg-slate-50">
              <div className="space-y-4">
                {/* Header */}
                <div className="bg-violet-100 rounded-lg p-4">
                  <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-1">1. Main Headline</p>
                  <div className="h-8 bg-violet-200 rounded w-3/4"></div>
                  <p className="text-xs text-slate-500 mt-2">"New Spring Wine Collection Now Available"</p>
                </div>

                {/* Hero Image */}
                <div className="bg-blue-100 rounded-lg p-4">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">2. Main Image</p>
                  <div className="h-24 bg-blue-200 rounded flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-blue-400" />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">A beautiful, high-quality photo at the top</p>
                </div>

                {/* Intro Paragraph */}
                <div className="bg-emerald-100 rounded-lg p-4">
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">3. Introduction</p>
                  <div className="space-y-1">
                    <div className="h-3 bg-emerald-200 rounded w-full"></div>
                    <div className="h-3 bg-emerald-200 rounded w-5/6"></div>
                    <div className="h-3 bg-emerald-200 rounded w-4/5"></div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">A short paragraph introducing your topic</p>
                </div>

                {/* Content Sections */}
                <div className="bg-amber-100 rounded-lg p-4">
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">4. Content Sections</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <div className="h-3 bg-amber-200 rounded w-full"></div>
                      <div className="h-3 bg-amber-200 rounded w-4/5"></div>
                      <div className="h-3 bg-amber-200 rounded w-5/6"></div>
                    </div>
                    <div className="h-16 bg-amber-200 rounded flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-amber-400" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">More paragraphs with optional images</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Do's Section */}
        <Card className="bg-white border-slate-100 rounded-2xl mb-8 print:border print:border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700" style={{fontFamily: 'Manrope'}}>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              What Works Great
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Clear Headlines & Subheadings</p>
                    <p className="text-sm text-slate-600">Use H1, H2, H3 tags in your page. These help us identify your main topics.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Regular Paragraphs</p>
                    <p className="text-sm text-slate-600">Normal text paragraphs are perfect. We'll turn each one into an email section.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">High-Quality Images</p>
                    <p className="text-sm text-slate-600">JPG or PNG images, at least 600px wide. The bigger, the better!</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Layout className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Simple Page Layouts</p>
                    <p className="text-sm text-slate-600">Standard blog-style layouts work best. Text flows top to bottom.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ExternalLink className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Public, Accessible Pages</p>
                    <p className="text-sm text-slate-600">Make sure the page is live and doesn't require a login to view.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Lists & Bullet Points</p>
                    <p className="text-sm text-slate-600">These translate beautifully into email content.</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Don'ts Section */}
        <Card className="bg-white border-slate-100 rounded-2xl mb-8 print:border print:border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700" style={{fontFamily: 'Manrope'}}>
              <XCircle className="w-5 h-5 text-red-500" />
              What to Avoid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Text Inside Images</p>
                    <p className="text-sm text-slate-600">We can't read text that's part of an image. Keep important info as regular text.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Password-Protected Pages</p>
                    <p className="text-sm text-slate-600">Our system needs to be able to access the page without logging in.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Heavy Animations / Flash</p>
                    <p className="text-sm text-slate-600">Animated content and videos can't be captured for email.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Very Long Pages</p>
                    <p className="text-sm text-slate-600">Extremely long pages might time out. Aim for 5-10 paragraphs maximum.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">PDF Links (Instead of Pages)</p>
                    <p className="text-sm text-slate-600">Link to a regular web page, not a downloadable PDF file.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Broken Images</p>
                    <p className="text-sm text-slate-600">Make sure all images on your page load properly before using it.</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Real Example Section */}
        <Card className="bg-white border-slate-100 rounded-2xl mb-8 print:border print:border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
              <Lightbulb className="w-5 h-5 text-amber-500" />
              A Real-World Example
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-slate-600">
              Let's say you run a winery and want to announce a new wine. Here's how your page should look:
            </p>

            {/* Example Structure */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-2">Headline (H1)</p>
                  <h3 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    Introducing Our 2024 Reserve Cabernet
                  </h3>
                </div>

                <div className="border-l-4 border-violet-300 pl-4">
                  <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-2">Hero Image</p>
                  <div className="bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg h-32 flex items-center justify-center">
                    <span className="text-slate-500">Beautiful photo of the wine bottle</span>
                  </div>
                </div>

                <div className="border-l-4 border-emerald-300 pl-4">
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2">Opening Paragraph</p>
                  <p className="text-slate-700">
                    After three years of careful aging, we're thrilled to release our most anticipated wine 
                    yet. The 2024 Reserve Cabernet showcases the best of our estate vineyards...
                  </p>
                </div>

                <div className="border-l-4 border-amber-300 pl-4">
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">Subheading + Details (H2)</p>
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">Tasting Notes</h4>
                  <p className="text-slate-700">
                    Rich aromas of blackberry and oak, with hints of vanilla and a long, smooth finish. 
                    Perfect for pairing with grilled meats or aged cheeses...
                  </p>
                </div>

                <div className="border-l-4 border-blue-300 pl-4">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">Another Section (H2)</p>
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">Limited Availability</h4>
                  <p className="text-slate-700">
                    Only 500 cases produced. Reserve yours today through our wine club 
                    or visit our tasting room...
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <p className="text-sm text-emerald-800">
                <strong>Result:</strong> This page structure will automatically generate an email with a catchy subject line, 
                the wine photo as the header, and 3-4 beautifully formatted content sections!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tips Section */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100 rounded-2xl mb-8 print:bg-amber-50 print:border print:border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Pro Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">1.</span>
                <span className="text-slate-700"><strong>Test your page first.</strong> Open it in a private/incognito browser window to make sure it loads without being logged in.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">2.</span>
                <span className="text-slate-700"><strong>Use descriptive image names.</strong> Instead of "IMG_1234.jpg", use "spring-wine-release.jpg".</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">3.</span>
                <span className="text-slate-700"><strong>Keep it focused.</strong> One topic per page works better than cramming everything in.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">4.</span>
                <span className="text-slate-700"><strong>Preview before sending.</strong> Always use our preview feature to see how the email will look!</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Final CTA */}
        <div className="text-center py-8 print:hidden">
          <p className="text-slate-600 mb-4">Ready to create your email?</p>
          <Button
            onClick={() => navigate("/campaign/new")}
            className="bg-violet-600 hover:bg-violet-700 rounded-full px-8 h-12 font-semibold"
            data-testid="create-campaign-btn"
          >
            Create New Campaign
          </Button>
        </div>

        {/* Print Footer */}
        <div className="hidden print:block text-center text-sm text-slate-500 border-t border-slate-200 pt-4 mt-8">
          <p>AI Email Generator - Best Practices Guide</p>
          <p>Questions? Contact your advertising agency for help.</p>
        </div>
      </main>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default HelpPage;
